/**
 * WebAudio-synthesized audio for Monkey Wheel — no asset files needed (CC0 by
 * nature). Lazily creates the AudioContext on first user gesture so autoplay
 * policies are respected. A single persisted mute flag governs both the SFX and
 * the ambient background music. Tones lean bright/tropical to match the jungle
 * theme.
 */

let ctx = null;
let muted = false;
try { muted = localStorage.getItem('mw_muted') === '1'; } catch (_) {}

function ac() {
  if (!ctx) {
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch (_) { ctx = null; }
  }
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

export function isMuted() { return muted; }
export function setMuted(m) {
  muted = !!m;
  try { localStorage.setItem('mw_muted', muted ? '1' : '0'); } catch (_) {}
  applyMusicMute();           // music ducks/restores with the same toggle
}
export function toggleMuted() { setMuted(!muted); return muted; }
/** Prime the audio context on first gesture and start ambient music. */
export function unlockAudio() { ac(); startMusic(); }

function tone({ freq = 440, dur = 0.12, type = 'sine', gain = 0.18, sweep = 0, delay = 0 }) {
  const a = ac();
  if (!a || muted) return;
  const t0 = a.currentTime + delay;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweep) osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq + sweep), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(a.destination);
  osc.start(t0); osc.stop(t0 + dur + 0.02);
}

function noise({ dur = 0.08, gain = 0.12 }) {
  const a = ac();
  if (!a || muted) return;
  const t0 = a.currentTime;
  const buf = a.createBuffer(1, a.sampleRate * dur, a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = a.createBufferSource();
  const g = a.createGain();
  g.gain.value = gain;
  src.buffer = buf; src.connect(g); g.connect(a.destination);
  src.start(t0);
}

export const sfx = {
  // Soft UI press for buttons / chip / menu taps.
  tap: () => tone({ freq: 520, dur: 0.04, type: 'sine', gain: 0.07 }),
  // Bet placed — bright two-tone "coin drop".
  bet: () => { tone({ freq: 880, dur: 0.05, type: 'triangle', gain: 0.12 }); tone({ freq: 1320, dur: 0.06, type: 'triangle', gain: 0.08, delay: 0.04 }); },
  // Wheel spin start — a quick rising whirr plus a tick burst.
  spin: () => {
    duckMusic();
    tone({ freq: 300, dur: 0.5, type: 'sawtooth', gain: 0.1, sweep: 900 });
    for (let i = 0; i < 6; i++) tone({ freq: 1200, dur: 0.03, type: 'square', gain: 0.05, delay: i * 0.06 });
  },
  // Win — happy ascending major arpeggio.
  win: () => { duckMusic(); [523, 659, 784, 1047, 1319].forEach((f, i) => tone({ freq: f, dur: 0.2, type: 'triangle', gain: 0.17, delay: i * 0.09 })); },
  // Lose — short descending tones.
  lose: () => { duckMusic(); [392, 330, 262].forEach((f, i) => tone({ freq: f, dur: 0.22, type: 'sine', gain: 0.12, delay: i * 0.12 })); },
};

/* ============ Ambient background music ============
 * A relaxing lo-fi chill loop: three soft detuned pad voices through a warm,
 * heavily low-passed filter; a slow, mellow ii–V–I–vi progression (Am7 → Dm7 →
 * G → Cmaj7); a barely-there "breathing" LFO; and one quiet, sparse pluck
 * arpeggio that gives the loop a subtle, addictive melodic hook. Fully
 * synthesized (no assets), lightweight, and muted by the same flag as the SFX. */

const MUSIC_LEVEL = 0.05;
let music = null;

// Mellow lo-fi progression — each pad voice takes one chord tone (Hz).
// Am7 → Dm7 → G → Cmaj7, voiced low and warm.
const CHORDS = [
  [220.00, 261.63, 329.63], // Am7  (A  C  E)
  [220.00, 293.66, 349.23], // Dm7  (A  D  F)
  [196.00, 293.66, 349.23], // G    (G  D  F)
  [196.00, 261.63, 329.63], // Cmaj7(G  C  E)
];

// One sparse pluck note per chord (an octave up) — the gentle melodic hook.
const ARP = [659.25, 587.33, 523.25, 493.88]; // E5  D5  C5  B4

export function startMusic() {
  const a = ac();
  if (!a || music) return;

  const master = a.createGain();
  master.gain.value = muted ? 0 : MUSIC_LEVEL;
  master.connect(a.destination);

  // Warm, muffled low-pass for that lo-fi pad tone.
  const filter = a.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 760;
  filter.Q.value = 0.5;
  filter.connect(master);

  // Very slow, shallow breathing LFO modulating the master gain.
  const lfo = a.createOscillator();
  const lfoGain = a.createGain();
  lfo.frequency.value = 0.05;
  lfoGain.gain.value = muted ? 0 : MUSIC_LEVEL * 0.25;
  lfo.connect(lfoGain); lfoGain.connect(master.gain);
  lfo.start();

  const voices = [];
  for (let i = 0; i < 3; i++) {
    const osc = a.createOscillator();
    osc.type = i === 0 ? 'sine' : 'triangle';
    osc.detune.value = (i - 1) * 5;
    const g = a.createGain();
    g.gain.value = 0.28;
    osc.connect(g); g.connect(filter);
    osc.start();
    voices.push(osc);
  }

  // Sparse pluck voice: its own warmer filter so it sits softly above the pad.
  const arpFilter = a.createBiquadFilter();
  arpFilter.type = 'lowpass';
  arpFilter.frequency.value = 1400;
  arpFilter.connect(master);

  let ci = 0;
  const playArp = (freq) => {
    if (muted) return;
    const t0 = a.currentTime + 0.05;
    const osc = a.createOscillator();
    const g = a.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.05, t0 + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.6);
    osc.connect(g); g.connect(arpFilter);
    osc.start(t0); osc.stop(t0 + 1.7);
  };

  const applyChord = () => {
    const c = CHORDS[ci % CHORDS.length];
    const t = a.currentTime;
    // Long glide between chords so transitions are smooth, never abrupt.
    voices.forEach((osc, k) => osc.frequency.setTargetAtTime(c[k], t, 1.8));
    playArp(ARP[ci % ARP.length]);
    ci++;
  };
  applyChord();
  const progTimer = setInterval(applyChord, 7500);

  music = { master, lfo, lfoGain, voices, filter, arpFilter, progTimer };
}

export function stopMusic() {
  if (!music) return;
  const m = music; music = null;
  clearInterval(m.progTimer);
  const a = ctx;
  try {
    const t = a ? a.currentTime : 0;
    m.master.gain.setTargetAtTime(0, t, 0.2);
  } catch (_) {}
  setTimeout(() => {
    try {
      m.voices.forEach((o) => o.stop());
      m.lfo.stop();
      m.master.disconnect();
    } catch (_) {}
  }, 450);
}

/** Ramp music to 0 (muted) or its base level — keeps the graph alive. */
function applyMusicMute() {
  if (!music || !ctx) return;
  const t = ctx.currentTime;
  music.master.gain.setTargetAtTime(muted ? 0 : MUSIC_LEVEL, t, 0.15);
  music.lfoGain.gain.setTargetAtTime(muted ? 0 : MUSIC_LEVEL * 0.45, t, 0.15);
}

/** Briefly dip the music under a win/lose/spin stinger, then restore. */
function duckMusic() {
  if (!music || !ctx || muted) return;
  const t = ctx.currentTime;
  music.master.gain.cancelScheduledValues(t);
  music.master.gain.setTargetAtTime(MUSIC_LEVEL * 0.3, t, 0.1);
  music.master.gain.setTargetAtTime(MUSIC_LEVEL, t + 1.1, 0.4);
}
